import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { Asset, AssetCollection, AssetFilter, AssetStatistics } from '../types';

/** Authenticated, read-only register. Failures never substitute sample assets. */
export class AssetRegisterService {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async request<T>(path: string): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('it-asset-register');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`Asset Register request failed (${response.status})`);
    }
    return response.json();
  }

  async getAssets(filter?: AssetFilter): Promise<AssetCollection> {
    const remote = await this.request<AssetCollection>('/');
    let assets = [...remote.assets];
    if (filter) {
      if (filter.category) {
        assets = assets.filter(a => a.category === filter.category);
      }
      if (filter.status) {
        assets = assets.filter(a => a.status === filter.status);
      }
      if (filter.owner) {
        assets = assets.filter(a =>
          a.owner.toLowerCase().includes(filter.owner!.toLowerCase()),
        );
      }
      if (filter.location) {
        assets = assets.filter(a =>
          a.location.toLowerCase().includes(filter.location!.toLowerCase()),
        );
      }
      if (filter.search) {
        const searchLower = filter.search.toLowerCase();
        assets = assets.filter(
          a =>
            a.name.toLowerCase().includes(searchLower) ||
            a.assetTag.toLowerCase().includes(searchLower) ||
            (a.serialNumber &&
              a.serialNumber.toLowerCase().includes(searchLower)),
        );
      }
    }

    return {
      assets,
      total_entries: assets.length,
    };
  }

  async getAsset(id: string): Promise<Asset | undefined> {
    return this.request<Asset>(`/${encodeURIComponent(id)}`);
  }

  async getStatistics(): Promise<AssetStatistics> {
    return this.request<AssetStatistics>('/statistics');
  }

  async addAsset(
    _asset: Omit<Asset, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<Asset> {
    throw new Error(
      'The Asset Register is read-only; update the controlled inventory source.',
    );
  }
}
